"use client";

import { useState } from "react";

import { archiveChartSubject, type AccountChartSubject, type AccountReading } from "./account-api";
import {
  AddChartSubjectForm,
  chartRelationshipLabel,
  CompareChartSubjects,
  StartChartCircle,
} from "./AccountChartCircleActions";
import { formatAccountDate } from "./account-format";

function SubjectCard({
  subject,
  busy,
  onRemove,
}: {
  subject: AccountChartSubject;
  busy: boolean;
  onRemove: (subject: AccountChartSubject) => void;
}) {
  return (
    <article className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-4 shadow-[var(--shadow-elevation-low)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-[var(--color-accent)]">
            {chartRelationshipLabel(subject.relationship_to_owner)}
          </p>
          <h3 className="mt-1 truncate font-serif text-xl text-[var(--color-text-main)]">
            {subject.display_name}
          </h3>
          <p className="mt-1 text-xs leading-5 text-[var(--color-text-sub)]">
            {formatAccountDate(subject.birth_date)}
            {subject.birth_place ? ` · ${subject.birth_place}` : ""}
          </p>
        </div>
        <span
          aria-hidden
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] font-serif text-lg text-[var(--color-accent)]"
        >
          {subject.relationship_to_owner === "self" ? "ॐ" : "✦"}
        </span>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <a
          href={`/kundali/${encodeURIComponent(subject.reading_id)}/${
            subject.language === "hi" ? "?lang=hi" : ""
          }`}
          className="inline-flex min-h-11 items-center text-sm font-semibold text-[var(--color-accent)]"
        >
          View kundali
        </a>
        {subject.relationship_to_owner !== "self" ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onRemove(subject)}
            className="min-h-11 text-sm font-semibold text-[var(--color-text-sub)] disabled:opacity-50"
          >
            Remove
          </button>
        ) : null}
      </div>
    </article>
  );
}

function SubjectGrid({
  subjects,
  token,
  onRefresh,
}: {
  subjects: AccountChartSubject[];
  token: string;
  onRefresh: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function remove(subject: AccountChartSubject) {
    setBusy(true);
    setMessage("");
    try {
      await archiveChartSubject(token, subject.subject_id);
      await onRefresh();
      setMessage(`${subject.display_name}'s chart was removed.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not remove the saved chart.");
    } finally {
      setBusy(false);
    }
  }

  if (!subjects.length) return null;
  return (
    <>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {subjects.map((subject) => (
          <SubjectCard
            key={subject.subject_id}
            subject={subject}
            busy={busy}
            onRemove={(item) => void remove(item)}
          />
        ))}
      </div>
      <p aria-live="polite" className="mt-2 min-h-5 text-xs text-[var(--color-text-sub)]">
        {message}
      </p>
    </>
  );
}

export function AccountChartCircle({
  subjects,
  readings,
  token,
  onRefresh,
}: {
  subjects: AccountChartSubject[];
  readings: AccountReading[];
  token: string;
  onRefresh: () => Promise<void>;
}) {
  const selfSubject = subjects.find((subject) => subject.relationship_to_owner === "self");
  return (
    <section className="mb-8" aria-labelledby="chart-circle-heading">
      <div className="rounded-xl border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] p-5 sm:p-6">
        <p className="text-eyebrow font-semibold uppercase tracking-eyebrow text-[var(--color-accent)]">
          Family chart circle
        </p>
        <h2 id="chart-circle-heading" className="mt-1 font-serif text-2xl">
          One private place for the charts you consult
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--color-text-sub)]">
          Save a family member&apos;s existing private kundali, then compare two charts without
          entering birth details again. Charts claimed by another verified account cannot be added.
        </p>
        {!selfSubject && readings[0] ? (
          <StartChartCircle reading={readings[0]} token={token} onRefresh={onRefresh} />
        ) : null}
        <SubjectGrid subjects={subjects} token={token} onRefresh={onRefresh} />
        <AddChartSubjectForm enabled={Boolean(selfSubject)} token={token} onRefresh={onRefresh} />
        <CompareChartSubjects subjects={subjects} token={token} />
      </div>
    </section>
  );
}
